import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import {
  FiPlus, FiEdit2, FiTrash2, FiX, FiSave, FiSearch, FiArrowLeft,
  FiCheck, FiXCircle, FiEye, FiFileText, FiPrinter, FiLink
} from 'react-icons/fi';
import { colors, spacing, borderRadius, shadows, typography } from '../../theme/colors';
import LinkedDocNumbers, { parentLinks } from '../../components/LinkedDocNumbers';
import {
  ActionBar, SearchBox, FilterGroup, StatusFilterSelect, TranAddButton, LoadingMessage,
  TranEmptyState, TableContainer, Table, Th, Tr, Td, StatusBadge, ActionGroup, IconBtn,
  SummaryBar, FormHeader, BackButton, FormTitle, FormActions, SaveButton, FormCard,
  SectionTitle, FormGrid, FormGroup, Label, Input, Select, TextArea, InfoGrid, InfoItem,
  NotesText, LineItemsHeader, AddLineButton, LineTableContainer, LineTable, LineTh, LineTd,
  LineInput, RemoveLineBtn, TotalsSection, TotalsTable, TotalRow, BalanceRow, DiscountInput,
  PaymentSection, SortableTh, SortContext
} from '../../components/SharedStyles';
import SizeLineRow from '../../components/SizeLineRow';
import { useAuth } from '../../context/AuthContext';
import { missingLocation, reqLabel, LOCATION_REQUIRED_MSG, locationCompulsory } from '../../utils/locationRequired';
import { formatAmount } from '../../utils/documentNumbering';
import LineDiscountRows from '../../components/LineDiscountRows';
import { orderDiscountShare, orderLineNetValue } from '../../utils/purchaseDiscount';
import { printDocument, loadDocumentFormats } from '../../utils/printTemplates';
import {
  createTransaction,
  updateTransaction,
  deleteTransaction,
  getTransactions,
  getTransaction,
  updateTransactionStatus
} from '../../utils/transactionHelper';
import { getItemStockCached, invalidateItemStock, getRecentItemPrices, getAvailableSerials } from '../../utils/stockFlowHelper';
import { getVendorsCached, getItemsCached, getAccountsCached, getEmployeesCached, getJobsCached, getLocationsCached } from '../../utils/masterDataStore';
import ItemPriceHints from '../../components/ItemPriceHints';
import { recomputeSizeLine } from '../../utils/sizeHelper';
import PageLayout from '../../components/PageLayout';
import SendWhatsAppButton from '../../components/SendWhatsAppButton';
import VendorSelect from '../../components/VendorSelect';
import { useVendorPricing, getVendorPrice } from '../../utils/vendorPricingHelper';
import ItemSelect from '../../components/ItemSelect';
import QuickAddItemModal from '../../components/QuickAddItemModal';
import {
  checkDuplicateVendorInvoice, isFutureDate, isLargeTransaction,
  LARGE_TRANSACTION_THRESHOLD, writeAuditLog
} from '../../utils/validationHelpers';
import useDocumentShortcuts from '../../hooks/useDocumentShortcuts';
import usePagePermissions from '../../hooks/usePagePermissions';
import useTableSort from '../../hooks/useTableSort';
import useDocListPagination from '../../hooks/useDocListPagination';
import DocListPagination from '../../components/DocListPagination';
import AttachmentPanel from '../../components/AttachmentPanel';
import { startSaveTimer } from '../../utils/saveTimer';
import { prewarmSave } from '../../utils/transactionHelper';
import { percentForDiscountAmount } from '../../utils/discountPercent';
import TwoUnitQtyBox, { TwoUnitNote } from '../../components/TwoUnitQtyBox';
import { twoUnitSwitchQty } from '../../utils/unitConversion';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { inActiveBranch } from '../../utils/branchLink';

const TRAN_TYPE_CODE = 'PI';

// ── PO receiving progress (FLW-01) ──────────────────────────────────────────
// An invoice made straight from a PO counts on it while Posted / Partial / Paid.
// Each invoice line keeps the PO line it came from (poLineNumber = that line's
// number on the PO), so a partly received PO is updated on the right lines;
// older invoices without it fall back to the same item. Progress is written
// straight onto the PO — only lines / status / updatedAt / updatedBy, the
// receiving fields the security rules let a purchase-invoice user change.
const INVOICE_LIVE = ['Posted', 'Partial', 'Paid'];
const PO_PROGRESS_STATUSES = ['Approved', 'Partially Received', 'Completed'];
const r6 = (n) => Math.round((Number(n) || 0) * 1e6) / 1e6;
const poLineNo = (pl, idx) => Number(pl?.lineNumber) || (idx + 1);

const findPOLineIndex = (poLines, ref, itemId, qty, removing) => {
  const n = Number(ref) || 0;
  if (n > 0) {
    const i = poLines.findIndex((pl, idx) => poLineNo(pl, idx) === n);
    if (i >= 0 && (!itemId || !poLines[i].itemId || poLines[i].itemId === itemId)) return i;
  }
  if (!itemId) return -1;
  const same = [];
  poLines.forEach((pl, idx) => { if (pl.itemId === itemId) same.push(idx); });
  const fit = same.find(idx => (removing
    ? (Number(poLines[idx].receivedQty) || 0) >= qty
    : (Number(poLines[idx].quantity) || 0) - (Number(poLines[idx].receivedQty) || 0) > 0));
  return fit !== undefined ? fit : (same.length ? same[0] : -1);
};

// Invoice lines → { ref, itemId, qty } entries for the PO.
export const invoicePOEntries = (lines) => (lines || []).map(l => ({
  ref: l.poLineNumber, itemId: l.itemId || '', qty: Number(l.quantity) || 0
}));

// Adds (sign 1) or takes off (sign -1) received quantities on a copy of the PO lines.
export const shiftPOReceived = (poLines, entries, sign) => {
  const lines = (poLines || []).map(l => ({ ...l }));
  (entries || []).forEach(({ ref, itemId, qty }) => {
    if (!(qty > 0)) return;
    const i = findPOLineIndex(lines, ref, itemId, qty, sign < 0);
    if (i < 0) return;
    const rec = Math.max(0, r6((Number(lines[i].receivedQty) || 0) + sign * qty));
    lines[i] = { ...lines[i], receivedQty: rec, pendingQty: r6((Number(lines[i].quantity) || 0) - rec) };
  });
  return lines;
};

// Approved (nothing received) / Partially Received / Completed (all received).
// A PO in any other status (Draft, Cancelled) keeps it.
export const poStatusFromLines = (lines, current) => {
  if (!PO_PROGRESS_STATUSES.includes(current)) return current;
  const all = lines.length > 0 && lines.every(l => (Number(l.receivedQty) || 0) >= (Number(l.quantity) || 0));
  const some = lines.some(l => (Number(l.receivedQty) || 0) > 0);
  return all ? 'Completed' : some ? 'Partially Received' : 'Approved';
};

// Applies [{ entries, sign }] to the PO in one write (1 read + 1 write).
export const applyPOProgress = async (companyId, poId, changes, userId) => {
  const ref = doc(db, 'companies', companyId, 'transactions', poId);
  const snap = await getDoc(ref);
  if (!snap.exists() || snap.data().tranTypeCode !== 'PO') return null;
  const po = snap.data();
  let lines = po.lines || [];
  (changes || []).forEach(({ entries, sign }) => { lines = shiftPOReceived(lines, entries, sign); });
  const upd = { lines, updatedAt: new Date().toISOString(), ...(userId ? { updatedBy: userId } : {}) };
  const status = poStatusFromLines(lines, po.status);
  if (status !== po.status) upd.status = status;
  await updateDoc(ref, upd);
  return { ...po, ...upd };
};

// Save / edit: take the invoice's previous quantities off (if it counted), then
// add the new ones (if it counts now). Same PO → one write.
export const syncInvoicePOProgress = async (companyId, oldInv, newInv, userId) => {
  const oldPOId = (oldInv && oldInv.parentTranType === 'PO' && oldInv.parentId && INVOICE_LIVE.includes(oldInv.status)) ? oldInv.parentId : null;
  const newPOId = (newInv && newInv.parentTranType === 'PO' && newInv.parentId && INVOICE_LIVE.includes(newInv.status)) ? newInv.parentId : null;
  const takeOff = oldPOId ? { entries: invoicePOEntries(oldInv.lines), sign: -1 } : null;
  const addOn = newPOId ? { entries: invoicePOEntries(newInv.lines), sign: 1 } : null;
  if (oldPOId && oldPOId === newPOId) {
    await applyPOProgress(companyId, newPOId, [takeOff, addOn], userId);
    return;
  }
  if (oldPOId) await applyPOProgress(companyId, oldPOId, [takeOff], userId);
  if (newPOId) await applyPOProgress(companyId, newPOId, [addOn], userId);
};

// Footer summary spec — aggregated server-side in paged mode (~few reads),
// computed from the loaded list in filtered mode. Matches the old footer.
const SUMMARY_SPEC = {
  statuses: ['Draft', 'Posted', 'Paid'],
  outstandingStatuses: ['Posted', 'Partial'],
  outstandingField: 'balanceAmount'
};

const EMPTY_LINE = {
  itemId: '',
  itemCode: '',
  itemName: '',
  accountId: '',
  // Per-line expense account (module `purchases_line_expense_accounts`).
  // Only ever set on SERVICE lines — those post no stock, so books can't diverge.
  expenseAccountId: '',
  expenseAccountCode: '',
  expenseAccountName: '',
  description: '',
  unitOfMeasure: '',
  unitCode: '',
  quantity: 1,
  unitPrice: 0,
  discountPercent: 0,
  discountAmount: 0,
  taxable: false,
  taxRate: 0,
  taxAmount: 0,
  mrp: 0,
  lineTotal: 0,
  trackingType: 'None',
  batchNumber: '',
  expiryDate: '',
  serialNumbers: [],
  sourceDocId: '',
  sourceDocNumber: '',
  sourceDocDate: '',
  sourceLineNumber: 0
};

const STATUS_COLORS = {
  Draft: colors.warning.main,
  Posted: colors.info.main,
  Partial: '#9C27B0',
  Paid: colors.success.main,
  Cancelled: colors.error.main,
};

const PurchaseInvoice = () => {
  const location = useLocation();
  const { activeCompany, activeFinancialYear, currentUser, activeBranch, isModuleEnabled } = useAuth();
  // Speed: prepare the first save in the background as soon as this screen opens.
  useEffect(() => {
    if (!activeCompany?.id || !activeFinancialYear?.id) return;
    prewarmSave(activeCompany.id, {
      tranTypeCode: TRAN_TYPE_CODE,
      branchId: activeBranch?.id && activeBranch.id !== 'all' ? activeBranch.id : null,
      financialYearId: activeFinancialYear.id,
    });
  }, [activeCompany?.id, activeFinancialYear?.id, activeBranch?.id]);
  const multiUnitEnabled = isModuleEnabled && isModuleEnabled('master_units');
  const mrpEnabled = isModuleEnabled && isModuleEnabled('mrp_pricing');
  // Per-line expense account: SuperAdmin-gated, default OFF.
  const lineExpenseAccEnabled = isModuleEnabled && isModuleEnabled('purchases_line_expense_accounts');
  const { canEdit, canDelete, canCancel, canChange } = usePagePermissions('transactions', 'purchaseInvoice');
  const showBranchCol = activeBranch && activeBranch.id === 'all';

  const [printConfig, setPrintConfig] = useState(null);
  useEffect(() => { if (activeCompany?.id) loadDocumentFormats(activeCompany.id).then(setPrintConfig); }, [activeCompany]);

  const [viewMode, setViewMode] = useState('list');
  const sort = useTableSort('docDate', 'desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  // Paginated list (2026-07-16, Firebase cost): 20 docs/page from Firestore.
  // Any search / status filter / custom column sort switches to a one-time
  // full active-year load so filtering behaves exactly as before.
  const listFiltering = searchQuery.trim() !== '' || statusFilter !== 'All'
    || sort.sortField !== 'docDate' || sort.sortDir !== 'desc';
  const {
    docs: invoices,
    loading,
    refresh: loadInvoices,
    ensureDoc,
    applyLocal,
    summary: listSummary,
    pagination
  } = useDocListPagination({
    tranTypeCode: TRAN_TYPE_CODE,
    fullMode: listFiltering,
    summarySpec: SUMMARY_SPEC
  });

  const [editingInvoice, setEditingInvoice] = useState(null);
  // Quick-add-item (the "+" beside the line item selector)
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickAddIndex, setQuickAddIndex] = useState(null);
  const [pendingItemSelect, setPendingItemSelect] = useState(null);
  // Last-4-prices reference shown under each line item (read-only)
  const [priceRefs, setPriceRefs] = useState({});
  const priceReqRef = useRef({});
  const [viewingInvoice, setViewingInvoice] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    docDate: new Date().toISOString().split('T')[0],
    partyId: '',
    partyName: '',
    vendorInvoiceNo: '',
    vendorInvoiceDate: '',
    parentId: '',
    parentDocNumber: '',
    parentTranType: '', // 'GRN' or 'PO'
    parentIds: [], // multi-GRN: array of linked GRN ids (same vendor)
    poId: '',
    poNumber: '',
    dueDate: '',
    paymentTerms: '',
    currency: 'PKR',
    discountAmount: 0,
    whtRate: 0,
    whtAmount: 0,
    transportationCharges: 0,
    otherCharges: 0,
    notes: '',
    status: 'Draft',
    employeeId: '', employeeName: '', jobId: '', jobName: '',
    locationId: '', locationName: '',
    paymentAmount: 0, paymentAccountId: '', paymentAccountName: '',
    existingPaidAmount: 0,
    // Optional GL override — if blank, system defaults to Inventory Control (11301001)
    debitAccountId: '', debitAccountCode: '', debitAccountName: ''
  });
  const [lineItems, setLineItems] = useState([{ ...EMPTY_LINE }]);

  const [vendors, setVendors] = useState([]);
  const [items, setItems] = useState([]);
  const [approvedGRNs, setApprovedGRNs] = useState([]);
  const [approvedPOs, setApprovedPOs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [locations, setLocations] = useState([]);
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  // All Level-5 postable accounts — used for the optional debit-account override.
  const [allAccounts, setAllAccounts] = useState([]);
  // Line-level picker shows EXPENSE accounts only (5xxxx). allAccounts is already
  // level-5, party-linked excluded — just narrow it to expenses.
  const expenseAccounts = useMemo(
    () => allAccounts.filter(a => (a.code || '').startsWith('5')),
    [allAccounts]
  );
  // The expense-account column only applies to SERVICE (non-stock) lines.
  const isServiceLine = useCallback(
    (line) => items.find(i => i.id === line.itemId)?.itemType === 'service',
    [items]
  );
  const [stockBalances, setStockBalances] = useState({});

  // Invoice list now comes from useDocListPagination above (loadInvoices = its
  // refresh). The old full-history getTransactions load lived here.

  // Tier 1 (2026-07-27, Firebase cost): vendors/items/accounts come from the
  // device-local master cache (getMaster* → IndexedDB, 0 reads per open with a
  // throttled background change-check) instead of a full getDocs on every open.
  // Each screen keeps its own client-side filter/sort on the returned rows.
  const loadVendors = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getVendorsCached(activeCompany.id);
      setVendors([...rows].sort((a, b) => (a.vendorName || '').localeCompare(b.vendorName || '')));
    } catch (error) { console.error('Error loading vendors:', error); }
  }, [activeCompany]);

  const loadItems = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getItemsCached(activeCompany.id);
      setItems(rows.filter(i => i.isDeleted !== true).sort((a, b) => (a.itemName || '').localeCompare(b.itemName || '')));
    } catch (error) { console.error('Error loading items:', error); }
  }, [activeCompany]);

  // Firebase cost: stock is fetched ONE ITEM AT A TIME, on demand. The old
  // mount-time load scanned the company's ENTIRE stockFlow history just to draw
  // badges the user may never look at. Now the dropdown shows a "stock?" chip
  // and this runs only when the user taps it.
  const requestItemStock = useCallback(async (itemId) => {
    if (!activeCompany?.id || !itemId) return;
    try {
      const qty = await getItemStockCached(activeCompany.id, itemId, {
        branchId: (activeBranch && activeBranch.id !== 'all') ? activeBranch.id : undefined
      });
      setStockBalances(prev => ({ ...prev, [itemId]: qty }));
    } catch (e) { console.error('Error loading item stock:', e); }
  }, [activeCompany, activeBranch]);

  // ── D3 (2026-07-23, Firebase cost): the three loaders below feed ONLY the
  // form's "Link to GRN / Link to PO" dropdowns — nothing on the list view uses
  // them. They used to run on every page open AND after every save, each
  // scanning full document history (all GRNs + all POs + every PI *and* NPI
  // ever). They now load ONCE, lazily, the first time a form is actually
  // opened. List browsing and saving cost nothing.
  const formDataLoadedRef = useRef(false);
  // Share of each linked receipt's order Discount added to this invoice (receipt
  // id → amount), so removing the receipt takes its share off again.
  const grnDiscShareRef = useRef({});

  // Firebase cost: load ONLY still-open (not-yet-invoiced) GRNs. Invoicing a GRN
  // flips its status to 'Invoiced' (see handleSave), so 'Received' IS the linkable
  // set. This drops the 'Invoiced'-GRN read AND removes the need to scan the whole
  // PI/NPI history to detect already-invoiced ones.
  const loadApprovedGRNs = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const receivedGRNs = await getTransactions(activeCompany.id, { tranTypeCode: 'GRN', status: 'Received' });
      const byId = {};
      receivedGRNs.forEach(g => { byId[g.id] = g; });
      setApprovedGRNs(Object.values(byId));
    } catch (error) { console.error('Error loading GRNs:', error); }
  }, [activeCompany]);

  // Load approved POs (for direct PO → PI linking)
  const loadApprovedPOs = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const approvedList = await getTransactions(activeCompany.id, { tranTypeCode: 'PO', status: 'Approved' });
      const partialList = await getTransactions(activeCompany.id, { tranTypeCode: 'PO', status: 'Partially Received' });
      setApprovedPOs([...approvedList, ...partialList]);
    } catch (error) { console.error('Error loading POs:', error); }
  }, [activeCompany]);

  // Loads the form-only dropdown data once per page session (see D3 note above).
  const ensureFormData = useCallback(async () => {
    if (formDataLoadedRef.current || !activeCompany?.id) return;
    formDataLoadedRef.current = true;
    try {
      await Promise.all([loadApprovedGRNs(), loadApprovedPOs()]);
    } catch (error) {
      formDataLoadedRef.current = false; // let a later form open retry
      console.error('Error loading form link data:', error);
    }
  }, [activeCompany, loadApprovedGRNs, loadApprovedPOs]);

  const loadEmployees = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getEmployeesCached(activeCompany.id);
      setEmployees(rows.filter(r => r.isActive === true));
    } catch (error) { console.error('Error loading employees:', error); }
  }, [activeCompany]);

  const loadJobs = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getJobsCached(activeCompany.id);
      setJobs(rows.filter(r => r.isActive === true));
    } catch (error) { console.error('Error loading jobs:', error); }
  }, [activeCompany]);

  const loadLocations = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getLocationsCached(activeCompany.id);
      setLocations([...rows].sort((a, b) => (a.locationName || '').localeCompare(b.locationName || '')));
    } catch (error) { console.error('Error loading locations:', error); }
  }, [activeCompany]);

  const loadPaymentAccounts = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const allAccts = await getAccountsCached(activeCompany.id);
      const allLvl5 = allAccts.filter(a => a.level === 5 || a.isPostable);
      setPaymentAccounts(allLvl5.filter(a => a.code?.startsWith('11101') || a.code?.startsWith('11102')));
      // Debit-account override picker: assets (1xxxx) + expenses (5xxxx) — exclude
      // party-linked customer/vendor accounts to avoid accidental misposting.
      setAllAccounts(allLvl5.filter(a => {
        if (a.linkedEntityType === 'customer' || a.linkedEntityType === 'vendor') return false;
        return a.code?.startsWith('1') || a.code?.startsWith('5');
      }).sort((a, b) => (a.code || '').localeCompare(b.code || '')));
    } catch (error) { console.error('Error loading payment accounts:', error); }
  }, [activeCompany]);

  // NOTE: the invoice list itself loads inside useDocListPagination — not here.
  // D3: GRN/PO/cross-invoice loaders are NOT here anymore — they load lazily on
  // first form open (ensureFormData), since only the form uses them.
  useEffect(() => {
    loadVendors(); loadItems();
    loadEmployees(); loadJobs(); loadLocations(); loadPaymentAccounts();
  }, [loadVendors, loadItems, loadEmployees, loadJobs, loadLocations, loadPaymentAccounts]);

  // Filter
  const filteredInvoices = useMemo(() => {
    let filtered = invoices;
    if (statusFilter !== 'All') filtered = filtered.filter(inv => inv.status === statusFilter);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(inv =>
        inv.docNumber?.toLowerCase().includes(q) ||
        inv.partyName?.toLowerCase().includes(q) ||
        inv.vendorInvoiceNo?.toLowerCase().includes(q) ||
        inv.parentDocNumber?.toLowerCase().includes(q)
      );
    }
    return sort.sortItems(filtered);
  }, [invoices, statusFilter, searchQuery, sort]);

  // Calculate line. MRP is manually entered per line and contributes to lineTotal.
  // The subTransaction helper credits the MRP component to Inventory Control
  // (PI) so accounting stays balanced.
  const calculateLine = (line) => {
    const amount = line.quantity * line.unitPrice;
    const discAmt = amount * (line.discountPercent / 100);
    const net = amount - discAmt;
    const tax = line.taxable ? net * (line.taxRate / 100) : 0;
    const mrp = Number(line.mrp) || 0;
    return {
      ...line,
      discountAmount: Math.round(discAmt * 100) / 100,
      taxAmount: Math.round(tax * 100) / 100,
      lineTotal: Math.round((net + tax + mrp) * 100) / 100
    };
  };

  // Header totals
  const totals = useMemo(() => {
    const lines = lineItems.map(calculateLine);
    const subtotal = lines.reduce((s, l) => s + (l.quantity * l.unitPrice - l.discountAmount), 0);
    const tax = lines.reduce((s, l) => s + l.taxAmount, 0);
    const mrpTotal = lines.reduce((s, l) => s + (Number(l.mrp) || 0), 0);
    const disc = Number(formData.discountAmount) || 0;
    const transportationCharges = Number(formData.transportationCharges) || 0;
    const otherCharges = Number(formData.otherCharges) || 0;
    const beforeWht = subtotal - disc + tax + mrpTotal;
    const whtRate = Number(formData.whtRate) || 0;
    // WHT base: pre-tax (subtotal) by default, or tax-inclusive (subtotal + tax)
    // when the company enables "WHT on tax-inclusive amount" in My Company settings.
    const whtInclusive = typeof window !== 'undefined' && window.__AMAL_WHT_INCLUSIVE__ === true;
    const whtBase = whtInclusive ? (subtotal + tax) : subtotal;
    const wht = Math.round(whtBase * (whtRate / 100) * 100) / 100;
    // Transport + Other are non-taxable add-ons applied AFTER tax & WHT; they raise
    // the vendor payable and post to dedicated expense accounts (carriage inwards /
    // other charges) in subTransactionHelper generatePurchaseInvoiceEntries.
    return {
      subtotal: Math.round(subtotal * 100) / 100,
      taxAmount: Math.round(tax * 100) / 100,
      mrpTotal: Math.round(mrpTotal * 100) / 100,
      headerDiscount: disc,
      whtAmount: wht,
      transportationCharges: Math.round(transportationCharges * 100) / 100,
      otherCharges: Math.round(otherCharges * 100) / 100,
      totalAmount: Math.round((beforeWht - wht + transportationCharges + otherCharges) * 100) / 100
    };
  }, [lineItems, formData.discountAmount, formData.whtRate, formData.transportationCharges, formData.otherCharges]);

  // Form handlers
  // Add `n` days to an ISO date string (YYYY-MM-DD). Returns '' for invalid input.
  const addDays = (isoDate, n) => {
    const days = Number(n);
    if (!isoDate || !days || days <= 0) return '';
    const d = new Date(isoDate);
    if (Number.isNaN(d.getTime())) return '';
    d.setDate(d.getDate() + days);
    return d.toISOString().split('T')[0];
  };

  // When docDate changes, also recompute dueDate from the current party's credit days.
  const handleHeaderChange = (field, value) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'docDate' && prev.partyId) {
        const party = vendors.find(v => v.id === prev.partyId);
        const days = party?.paymentTerms || party?.creditDays || 0;
        if (days > 0) next.dueDate = addDays(value, days);
      }
      return next;
    });
  };

  const handleVendorChange = (vendorId, party) => {
    // Unified-mode aware: party may be a vendor OR a customer (when unified_party_picker is on).
    // We preserve the original partyType so accounting routes to the correct linked account.
    const resolved = party || vendors.find(v => v.id === vendorId);
    const isCustomer = resolved?._partyType === 'customer';
    // Vendors carry `paymentTerms`, customers (unified mode) carry `creditDays`.
    const days = Number(resolved?.paymentTerms || resolved?.creditDays) || 0;
    setFormData(prev => ({
      ...prev,
      partyId: vendorId,
      partyName: resolved?.vendorName || resolved?.companyName || '',
      partyType: isCustomer ? 'customer' : 'vendor',
      paymentTerms: days ? `${days} days` : prev.paymentTerms,
      dueDate: days ? addDays(prev.docDate, days) : prev.dueDate,
      parentId: '', parentDocNumber: '', parentTranType: '', poId: '', poNumber: '',
      // WHT only applies when party is a real vendor (customers don't have whtApplicable)
      whtRate: !isCustomer && resolved?.whtApplicable ? (resolved.whtRate || 0) : 0
    }));
    setLineItems([{ ...EMPTY_LINE }]);
  };

  // Add a GRN to the invoice — appends GRN lines tagged with source.
  // Vendor is locked to the first GRN's vendor; PO link is locked once GRNs are added.
  const addGRN = (grnId) => {
    if (!grnId) return;
    const grn = approvedGRNs.find(g => g.id === grnId);
    if (!grn) return;
    if (formData.parentIds.includes(grnId)) return;
    // Enforce same-vendor constraint
    if (formData.parentIds.length > 0 && formData.partyId && grn.partyId !== formData.partyId) {
      alert('All linked GRNs must belong to the same vendor.');
      return;
    }
    const isFirst = formData.parentIds.length === 0;
    const newLines = (grn.lines || []).map((l, idx) => ({
      ...EMPTY_LINE,
      itemId: l.itemId || '',
      itemCode: l.itemCode || '',
      itemName: l.itemName || '',
      description: l.description || l.itemName || '',
      unitOfMeasure: l.unitOfMeasure || '',
      quantity: l.acceptedQty || l.receivedQty || l.quantity || 0,
      unitPrice: l.unitCost || l.unitPrice || 0,
      taxable: !!l.taxable,
      taxRate: Number(l.taxRate) || 0,
      trackingType: l.trackingType || 'None',
      batchNumber: l.batchNumber || '',
      expiryDate: l.expiryDate || '',
      serialNumbers: l.serialNumbers || [],
      sourceDocId: grn.id,
      sourceDocNumber: grn.docNumber || '',
      sourceDocDate: grn.docDate || '',
      sourceLineNumber: l.lineNumber || (idx + 1)
    }));
    setFormData(prev => ({
      ...prev,
      parentIds: [...prev.parentIds, grnId],
      parentTranType: 'GRN',
      // Keep singular fields populated to first GRN for backward compat
      parentId: isFirst ? grnId : prev.parentId,
      parentDocNumber: isFirst ? (grn.docNumber || '') : prev.parentDocNumber,
      partyId: isFirst ? grn.partyId : prev.partyId,
      partyName: isFirst ? grn.partyName : prev.partyName,
      poId: isFirst ? (grn.parentId || '') : prev.poId,
      poNumber: isFirst ? (grn.parentDocNumber || '') : prev.poNumber,
      locationId: isFirst ? (grn.locationId || '') : prev.locationId,
      locationName: isFirst ? (grn.locationName || '') : prev.locationName
    }));
    // Append GRN lines. If the table currently holds only the default empty row, replace it.
    setLineItems(prev => {
      const hasOnlyEmpty = prev.length === 1 && !prev[0].itemId && !prev[0].description && !prev[0].sourceDocId;
      return hasOnlyEmpty ? newLines : [...prev, ...newLines];
    });
    // The receipt keeps the order's quantity and price but not its line
    // discount or GST — bring the order's discount % and GST onto these lines
    // once it is read (a discount or GST the user already set is left alone).
    grnLinePOTerms(grn).then(({ terms: byLine, share }) => {
      // The order's overall Discount: this receipt's share of it.
      if (share > 0) {
        grnDiscShareRef.current[grn.id] = share;
        setFormData(prev => (prev.parentIds.includes(grn.id)
          ? { ...prev, discountAmount: Math.round(((Number(prev.discountAmount) || 0) + share) * 100) / 100 }
          : prev));
      }
      if (!Object.keys(byLine).length) return;
      setLineItems(prev => prev.map(l => {
        const t = l.sourceDocId === grn.id ? byLine[l.sourceLineNumber] : null;
        if (!t) return l;
        const next = { ...l };
        if (t.discountPercent && !(Number(l.discountPercent) > 0)) next.discountPercent = t.discountPercent;
        if (t.taxable && !l.taxable && !(Number(l.taxRate) > 0)) { next.taxable = true; next.taxRate = t.taxRate; }
        return next;
      }));
    });
  };

  // { terms, share }: receipt line number → the order's line terms
  // { discountPercent, taxable, taxRate } ({} if none), and this receipt's share
  // of the order's overall Discount (0 if none). Uses the order already loaded
  // for the "Link to PO" list, else reads it (1 read).
  const grnLinePOTerms = async (grn) => {
    if (!grn?.parentId) return { terms: {}, share: 0 };
    let po = approvedPOs.find(p => p.id === grn.parentId);
    if (!po) {
      try {
        const snap = await getDoc(doc(db, 'companies', activeCompany.id, 'transactions', grn.parentId));
        if (snap.exists()) po = snap.data();
      } catch (e) {
        console.warn('Could not read the purchase order discounts:', e && e.message);
        return { terms: {}, share: 0 };
      }
    }
    if (!po || (po.tranTypeCode && po.tranTypeCode !== 'PO')) return { terms: {}, share: 0 };
    const byPoLine = {};
    const poLineByNo = {};
    (po.lines || []).forEach((pl, idx) => { poLineByNo[String(poLineNo(pl, idx))] = pl; });
    (po.lines || []).forEach((pl, idx) => {
      const d = Number(pl.discountPercent) || 0;
      const taxable = !!pl.taxable && (Number(pl.taxRate) || 0) > 0;
      if (d > 0 || taxable) byPoLine[String(poLineNo(pl, idx))] = { discountPercent: d, taxable, taxRate: taxable ? Number(pl.taxRate) : 0 };
    });
    const out = {};
    let partValue = 0;
    (grn.lines || []).forEach((l, idx) => {
      const t = byPoLine[String(l.poLineId || '')];
      if (t) out[l.lineNumber || (idx + 1)] = t;
      const qty = Number(l.acceptedQty || l.receivedQty || l.quantity) || 0;
      const pl = poLineByNo[String(l.poLineId || '')];
      partValue += pl ? orderLineNetValue(pl, qty) : qty * (Number(l.unitCost || l.unitPrice) || 0);
    });
    return { terms: out, share: orderDiscountShare(po, partValue) };
  };

  // Remove a GRN: strips its tagged lines and clears parent fields if it was the last.
  const removeGRN = (grnId) => {
    const remaining = formData.parentIds.filter(id => id !== grnId);
    const remainingLines = lineItems.filter(l => l.sourceDocId !== grnId);
    // Take this receipt's share of the order Discount off again.
    const share = grnDiscShareRef.current[grnId] || 0;
    delete grnDiscShareRef.current[grnId];
    if (share > 0) {
      setFormData(prev => ({ ...prev, discountAmount: Math.max(0, Math.round(((Number(prev.discountAmount) || 0) - share) * 100) / 100) }));
    }
    if (remaining.length === 0) {
      setFormData(prev => ({
        ...prev,
        parentIds: [],
        parentId: '',
        parentDocNumber: '',
        parentTranType: '',
        poId: '',
        poNumber: ''
      }));
      setLineItems([{ ...EMPTY_LINE }]);
      return;
    }
    // Re-derive head GRN fields from the now-first remaining GRN
    const firstId = remaining[0];
    const firstGrn = approvedGRNs.find(g => g.id === firstId);
    setFormData(prev => ({
      ...prev,
      parentIds: remaining,
      parentId: firstId,
      parentDocNumber: firstGrn?.docNumber || '',
      poId: firstGrn?.parentId || '',
      poNumber: firstGrn?.parentDocNumber || ''
    }));
    setLineItems(remainingLines.length ? remainingLines : [{ ...EMPTY_LINE }]);
  };

  // Order line → { trackingType, serialNumbers } for a serial-tracked item whose
  // order line carries serial numbers (imported from Excel). {} otherwise, so
  // every other order fills in exactly as before.
  const poSerialFields = (l) => {
    const item = items.find(i => i.id === l.itemId);
    if (item?.trackingType !== 'Serial' || !Array.isArray(l.serialNumbers) || !l.serialNumbers.length) return {};
    return { trackingType: 'Serial', serialNumbers: [...l.serialNumbers] };
  };
  const dropSerialsInStock = (poLines) => {
    poLines.filter(l => Number(l.receivedQty) > 0 && Object.keys(poSerialFields(l)).length)
      .forEach(l => {
        getAvailableSerials(activeCompany.id, l.itemId).then(inStock => {
          const have = new Set(inStock || []);
          setLineItems(prev => prev.map(x => (x.poLineNumber === l._poLineNo && Array.isArray(x.serialNumbers))
            ? { ...x, serialNumbers: x.serialNumbers.filter(s => !have.has(s)) } : x));
        }).catch(() => { /* keep the order's list; the save check flags any already in stock */ });
      });
  };

  const handlePOChange = async (poId) => {
    let po = approvedPOs.find(p => p.id === poId);
    if (!po) {
      setFormData(prev => ({ ...prev, parentId: '', parentDocNumber: '', poId: '', poNumber: '', parentTranType: '' }));
      return;
    }
    // FLW-01: the order list was loaded when the form first opened — read this PO
    // fresh (1 read) so lines already invoiced / received since are not offered again.
    try {
      const snap = await getDoc(doc(db, 'companies', activeCompany.id, 'transactions', poId));
      if (snap.exists()) po = { id: snap.id, ...snap.data() };
    } catch (e) { console.warn('Could not refresh the purchase order:', e && e.message); }
    // Auto-fill lines from PO (use pending qty). Each line keeps the PO line it
    // came from (FLW-01) so receiving progress lands on the right PO line.
    const poLines = (po.lines || []).map((l, idx) => ({ ...l, _poLineNo: poLineNo(l, idx) }))
      .filter(l => (l.pendingQty ?? (l.quantity - (l.receivedQty || 0))) > 0);
    // The order's overall Discount: the share of the part being invoiced (all of
    // it for the whole order). An order without one leaves the box as it was.
    const poDiscShare = orderDiscountShare(po, poLines.reduce(
      (s, l) => s + orderLineNetValue(l, l.pendingQty ?? (l.quantity - (l.receivedQty || 0))), 0));
    setFormData(prev => ({
      ...prev,
      parentId: poId,
      parentDocNumber: po.docNumber,
      parentTranType: 'PO',
      partyId: po.partyId,
      partyName: po.partyName,
      poId: poId,
      poNumber: po.docNumber,
      ...(poDiscShare > 0 ? { discountAmount: poDiscShare } : {})
    }));
    if (poLines.length > 0) {
      setLineItems(poLines.map(l => ({
        ...EMPTY_LINE,
        itemId: l.itemId || '',
        itemCode: l.itemCode || '',
        itemName: l.itemName || '',
        description: l.description || l.itemName || '',
        unitOfMeasure: l.unitOfMeasure || '',
        quantity: l.pendingQty ?? (l.quantity - (l.receivedQty || 0)),
        unitPrice: l.unitPrice || 0,
        discountPercent: Number(l.discountPercent) || 0, // the order's line discount
        taxable: !!l.taxable,                              // the order's line GST
        taxRate: Number(l.taxRate) || 0,
        // Serial numbers kept on the order (imported from Excel) fill in here
        // for serial-tracked items; part already received → serials in stock left out.
        ...poSerialFields(l),
        poLineNumber: l._poLineNo
      })));
      dropSerialsInStock(poLines);
    }
  };

  // When a quick-added item lands in the items list, drop it into the line that
  // triggered the add (runs after setItems, so the price/unit auto-fill works).
  useEffect(() => {
    if (pendingItemSelect && items.some(i => i.id === pendingItemSelect.itemId)) {
      handleLineChange(pendingItemSelect.index, 'itemId', pendingItemSelect.itemId);
      setPendingItemSelect(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, pendingItemSelect]);

  const handleQuickItemCreated = (newItem) => {
    setItems(prev => [...prev, newItem].sort((a, b) => (a.itemName || '').localeCompare(b.itemName || '')));
    if (quickAddIndex != null) setPendingItemSelect({ index: quickAddIndex, itemId: newItem.id });
    setShowQuickAdd(false);
    setQuickAddIndex(null);
  };

  // Load the last-4 PURCHASE prices for each distinct item on the lines (read-only).
  useEffect(() => {
    if (!activeCompany?.id) return;
    const ids = [...new Set(lineItems.map(l => l.itemId).filter(Boolean))];
    ids.forEach(id => {
      if (priceReqRef.current[id]) return;   // fetch once per item per session
      priceReqRef.current[id] = true;
      getRecentItemPrices(activeCompany.id, id, 'purchase').then(hints => {
        setPriceRefs(prev => ({ ...prev, [id]: hints || [] }));
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItems, activeCompany]);

  // Vendor-wise Items & Pricing (module purchases_vendor_pricing): the picked
  // vendor's price list. Empty — and nothing is read — while the module is off.
  const vendorPricing = useVendorPricing(activeCompany?.id, formData.partyId, isModuleEnabled);

  const handleLineChange = (index, field, value) => {
    setLineItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };

      // Line discount can be entered as % OR as a rupee amount (they sync). The %
      // stays the stored driver; typing an amount back-calculates the % so
      // calculateLine keeps deriving the amount from it and accounting stays exact.
      if (field === 'discountAmountInput') {
        const gross = (Number(updated[index].quantity) || 0) * (Number(updated[index].unitPrice) || 0);
        const amt = Math.max(0, Number(value) || 0);
        delete updated[index].discountAmountInput; // not a persisted field
        // Shortest % that gives back exactly the typed amount (4 decimals alone
        // could turn 70 into 69.99).
        updated[index].discountPercent = percentForDiscountAmount(gross, amt);
      }

      // Size-mode line edits: recompute Total SQft when L / H / Thickness / Pieces / sizeUnit change.
      if (updated[index].hasSize && ['length', 'height', 'thickness', 'pieces', 'sizeUnit'].includes(field)) {
        updated[index] = recomputeSizeLine(updated[index]);
      }

      // Carry the picked expense account's code/name onto the line.
      if (field === 'expenseAccountId') {
        const acc = expenseAccounts.find(a => a.id === value);
        updated[index].expenseAccountCode = acc?.code || '';
        updated[index].expenseAccountName = acc?.name || '';
      }

      if (field === 'itemId' && value) {
        const item = items.find(i => i.id === value);
        if (item) {
          // GUARD: an expense account is only valid on a SERVICE line (posts no
          // stock). If the line switches to a stock item, drop it — otherwise
          // stock would move while the value went to an expense account, and the
          // inventory ledger and GL would silently disagree.
          if (item.itemType !== 'service') {
            updated[index].expenseAccountId = '';
            updated[index].expenseAccountCode = '';
            updated[index].expenseAccountName = '';
          }
          updated[index].itemCode = item.itemCode || '';
          updated[index].itemName = item.itemName || '';
          updated[index].description = item.itemName || '';
          updated[index].unitOfMeasure = item.unitOfMeasure || 'Pcs';
          updated[index].unitPrice = item.purchasePrice || 0;
          updated[index].taxRate = item.gstRate || 0;
          updated[index].taxable = (item.gstRate || 0) > 0;
          updated[index].trackingType = item.trackingType || 'None';
          updated[index].batchNumber = '';
          updated[index].expiryDate = '';
          updated[index].serialNumbers = [];

          // Multi-unit seeding — pick defaultPurchaseUnit, else base unit.
          let seededUnit = item.unitOfMeasure || 'Pcs';
          if (Array.isArray(item.units) && item.units.length > 0) {
            const baseRow = item.units.find(u => u.isBase) || item.units[0];
            const defaultRow = item.defaultPurchaseUnit
              ? item.units.find(u => u.unitCode === item.defaultPurchaseUnit) || baseRow
              : baseRow;
            if (defaultRow) {
              seededUnit = defaultRow.unitCode;
              const conv = Number(defaultRow.conversion) || 1;
              updated[index].unitPrice = Number(defaultRow.purchasePrice) || (Number(item.purchasePrice) || 0) * conv;
            }
          }
          updated[index].unitOfMeasure = seededUnit;
          updated[index].unitCode = seededUnit;
          // Vendor's own price wins over the item's purchase price.
          const vp = getVendorPrice(vendorPricing, value, item, updated[index].unitCode);
          if (vp !== null) updated[index].unitPrice = vp;

          // Size mode: prime with item defaults and force unit to SQft.
          if (item.hasSize) {
            updated[index].hasSize = true;
            updated[index].sizeUnit = item.sizeUnit === 'cm' ? 'cm' : 'inches';
            updated[index].length = Number(item.defaultLength) || 0;
            updated[index].height = Number(item.defaultHeight) || 0;
            updated[index].thickness = Number(item.defaultThickness) || 0;
            updated[index].pieces = updated[index].pieces || 1;
            updated[index].unitOfMeasure = 'SQft';
            updated[index].unitCode = 'SQft';
            updated[index] = recomputeSizeLine(updated[index]);
          } else {
            updated[index].hasSize = false;
          }
        }
      }

      // Unit change — pull unit-specific purchasePrice if defined,
      // otherwise scale base purchasePrice by the conversion factor.
      if (field === 'unitCode' && updated[index].itemId) {
        const item = items.find(i => i.id === updated[index].itemId);
        if (item && Array.isArray(item.units)) {
          const unitRow = item.units.find(u => u.unitCode === value);
          if (unitRow) {
            updated[index].unitOfMeasure = unitRow.unitCode;
            const conv = Number(unitRow.conversion) || 1;
            updated[index].unitPrice = Number(unitRow.purchasePrice) || (Number(item.purchasePrice) || 0) * conv;
            const vp = getVendorPrice(vendorPricing, updated[index].itemId, item, value);
            if (vp !== null) updated[index].unitPrice = vp;
            // Show Two Units: switching "Rate per" between the item's two units keeps the same physical qty
            // (not on lines linked to another document — their qty is tracked against it).
            const twoQ = (formData.parentId || prev[index].sourceDocId || prev[index].poLineId) ? null
              : twoUnitSwitchQty(item, prev[index].unitCode || prev[index].unitOfMeasure, value, prev[index].quantity);
            if (twoQ !== null) updated[index].quantity = twoQ;
          }
        }
      }

      updated[index] = calculateLine(updated[index]);
      return updated;
    });
  };

  const addLine = () => setLineItems(prev => [...prev, { ...EMPTY_LINE }]);

  const lineItemRefs = useRef([]);
  const pendingFocusRowRef = useRef(null);
  useEffect(() => {
    if (pendingFocusRowRef.current == null) return;
    const rowIdx = pendingFocusRowRef.current;
    pendingFocusRowRef.current = null;
    setTimeout(() => {
      const el = lineItemRefs.current[rowIdx];
      if (el && typeof el.focus === 'function') el.focus();
    }, 50);
  }, [lineItems.length]);

  const handleLastFieldEnter = (e, index) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (formData.parentId) return;
    const isLastRow = index === lineItems.length - 1;
    const line = lineItems[index];
    const hasData = !!line.itemId || (Number(line.quantity) > 0 && Number(line.unitPrice) > 0);
    if (!hasData) return;
    if (isLastRow) {
      // eslint-disable-next-line no-alert
      if (window.confirm('Add a new line item?')) {
        pendingFocusRowRef.current = lineItems.length;
        addLine();
      }
    } else {
      const next = lineItemRefs.current[index + 1];
      if (next && typeof next.focus === 'function') next.focus();
    }
  };
  const removeLine = (index) => {
    if (lineItems.length <= 1) return;
    setLineItems(prev => prev.filter((_, i) => i !== index));
  };

  const resetForm = () => {
    setFormData({
      docDate: new Date().toISOString().split('T')[0],
      partyId: '', partyName: '', vendorInvoiceNo: '', vendorInvoiceDate: '',
      parentId: '', parentDocNumber: '', parentTranType: '', parentIds: [], poId: '', poNumber: '',
      dueDate: '', paymentTerms: '', currency: activeCompany?.currencyCode || 'PKR',
      discountAmount: 0, whtRate: 0, whtAmount: 0, transportationCharges: 0, otherCharges: 0, notes: '', status: 'Draft',
      employeeId: '', employeeName: '', jobId: '', jobName: '',
      locationId: '', locationName: '',
      paymentAmount: 0, paymentAccountId: '', paymentAccountName: '',
      existingPaidAmount: 0,
      debitAccountId: '', debitAccountCode: '', debitAccountName: ''
    });
    setLineItems([{ ...EMPTY_LINE }]);
    setEditingInvoice(null);
    grnDiscShareRef.current = {};
  };

  const handleCreate = () => { resetForm(); ensureFormData(); setViewMode('form'); };

  const handleEdit = (inv) => {
    if (!canChange) { alert('You can add new records but cannot change saved ones.'); return; } // Add-only role (2026-11-04)
    ensureFormData(); // D3: load GRN/PO link data on demand
    grnDiscShareRef.current = {};
    // Firebase cost: this invoice's linked GRN(s) are now status 'Invoiced', so
    // they are NOT in the open-GRN list. Fetch just those by id (a few reads, only
    // when opening a GRN-linked invoice) so their chips render on the form.
    const linkedIds = Array.isArray(inv.parentIds) && inv.parentIds.length > 0
      ? inv.parentIds
      : (inv.parentTranType === 'GRN' && inv.parentId ? [inv.parentId] : []);
    if (linkedIds.length > 0 && activeCompany?.id) {
      Promise.all(linkedIds.map(id => getTransaction(activeCompany.id, id).catch(() => null)))
        .then(docs => {
          const found = docs.filter(Boolean);
          if (found.length) setApprovedGRNs(prev => {
            const byId = {}; [...prev, ...found].forEach(g => { byId[g.id] = g; });
            return Object.values(byId);
          });
        });
    }
    setEditingInvoice(inv);
    setFormData({
      docDate: inv.docDate || new Date().toISOString().split('T')[0],
      partyId: inv.partyId || '', partyName: inv.partyName || '',
      partyType: inv.partyType || 'vendor',
      vendorInvoiceNo: inv.vendorInvoiceNo || '', vendorInvoiceDate: inv.vendorInvoiceDate || '',
      parentId: inv.parentId || '', parentDocNumber: inv.parentDocNumber || '',
      parentTranType: inv.parentTranType || '',
      parentIds: Array.isArray(inv.parentIds) && inv.parentIds.length > 0
        ? inv.parentIds
        : (inv.parentTranType === 'GRN' && inv.parentId ? [inv.parentId] : []),
      poId: inv.poId || '', poNumber: inv.poNumber || '',
      dueDate: inv.dueDate || '', paymentTerms: inv.paymentTerms || '',
      currency: inv.currency || 'PKR', discountAmount: inv.discountAmount || 0,
      whtRate: inv.whtRate || 0, whtAmount: inv.whtAmount || 0,
      transportationCharges: inv.transportationCharges || 0, otherCharges: inv.otherCharges || 0,
      notes: inv.notes || '', status: inv.status || 'Draft',
      employeeId: inv.employeeId || '', employeeName: inv.employeeName || '',
      jobId: inv.jobId || '', jobName: inv.jobName || '',
      locationId: inv.locationId || '', locationName: inv.locationName || '',
      paymentAmount: inv.paymentAmount || 0, paymentAccountId: inv.paymentAccountId || '', paymentAccountName: inv.paymentAccountName || '',
      existingReturnedAmount: Number(inv.returnedAmount) || 0, // ACC-11
      existingPaidAmount: Math.max(0, (inv.paidAmount || 0) - (inv.paymentAmount || 0)),
      debitAccountId: inv.debitAccountId || '',
      debitAccountCode: inv.debitAccountCode || '',
      debitAccountName: inv.debitAccountName || ''
    });
    setLineItems(inv.lines?.length > 0 ? inv.lines.map(l => ({ ...EMPTY_LINE, ...l })) : [{ ...EMPTY_LINE }]);
    setViewMode('form');
  };

  const handleView = (inv) => { setViewingInvoice(inv); setViewMode('view'); };

  // Handle navigation from reports (e.g., Journal Report clicking on document #).
  // With pagination the target may not be in the loaded page — ensureDoc
  // fetches that single document by id when needed.
  useEffect(() => {
    const targetId = location.state?.viewTransactionId;
    if (!targetId) return;
    ensureDoc(targetId).then(transaction => {
      if (transaction) {
        setViewingInvoice(transaction);
        setViewMode('view');
        // Clear the state to prevent re-triggering
        window.history.replaceState({}, document.title);
      }
    });
  }, [location.state, ensureDoc]);

  // Save invoice using transactionHelper
  const handleSave = async (saveStatus) => {
    const _screenTimer = startSaveTimer('Whole click: Purchase Invoice'); // save timer — see console
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    if (missingLocation(activeCompany, formData.locationId)) { alert(LOCATION_REQUIRED_MSG); return; }
    if (!formData.partyId) { alert('Please select a vendor'); return; }
    if (isModuleEnabled('purchases_enforce_grn_before_pi') && formData.parentTranType !== 'GRN') {
      alert('Company policy: every Purchase Invoice must be linked to a GRN. Please create a GRN first and link it here.');
      return;
    }
    if (!formData.vendorInvoiceNo && saveStatus === 'Posted') {
      alert('Vendor invoice number is required before posting');
      return;
    }
    if (!activeFinancialYear?.id) { alert('Please select an active financial year'); return; }
    if (Number(formData.paymentAmount) > 0 && !formData.paymentAccountId) {
      alert('Please select a payment account'); return;
    }

    const validLines = lineItems.filter(l => l.description && l.quantity > 0 && l.unitPrice > 0);
    if (validLines.length === 0) {
      alert('Please add at least one line item with description, quantity, and price');
      return;
    }

    for (const line of validLines) {
      if (line.trackingType === 'Batch' && !line.batchNumber?.trim()) {
        alert(`Batch number is required for "${line.itemName || line.description}"`); return;
      }
      if (line.trackingType === 'Serial') {
        const serials = line.serialNumbers || [];
        if (serials.length !== (line.quantity || 0)) {
          alert(`"${line.itemName || line.description}" requires exactly ${line.quantity} serial number(s). You entered ${serials.length}.`); return;
        }
        if (new Set(serials).size !== serials.length) {
          alert(`Duplicate serial numbers found for "${line.itemName || line.description}"`); return;
        }
      }
    }

    // Discount cap validation (max 50%)
    for (const line of validLines) {
      if (Number(line.discountPercent) > 50) {
        alert(`Discount on "${line.itemName || line.description}" exceeds 50%. Please review.`); return;
      }
    }

    // Future date warning
    if (isFutureDate(formData.docDate)) {
      if (!window.confirm('Document date is in the future. Continue?')) return;
    }

    // Zero-amount check when posting
    const preTotal = validLines.reduce((s, l) => s + (l.quantity * l.unitPrice), 0);
    if (saveStatus === 'Posted' && preTotal <= 0) {
      alert('Cannot post a zero-amount invoice'); return;
    }

    // Duplicate vendor invoice number check
    if (formData.vendorInvoiceNo && formData.partyId) {
      const isDuplicate = await checkDuplicateVendorInvoice(activeCompany.id, formData.partyId, formData.vendorInvoiceNo, editingInvoice?.id);
      if (isDuplicate) {
        if (!window.confirm('A purchase invoice with this vendor invoice number already exists for this vendor. Continue anyway?')) return;
      }
    }

    // Large transaction warning
    if (isLargeTransaction(preTotal)) {
      if (!window.confirm(`This is a large transaction (${activeCompany?.currencySymbol || 'Rs.'} ${preTotal.toLocaleString()}). Please verify the amount. Continue?`)) return;
    }

    setSaving(true);
    try {
      const calculatedLines = validLines.map((l, i) => {
        const calc = calculateLine(l);
        return {
          lineNumber: i + 1,
          itemId: l.itemId,
          itemCode: l.itemCode || '',
          itemName: l.itemName || '',
          description: l.description,
          quantity: l.quantity,
          unitPrice: l.unitPrice,
          unitCost: l.unitPrice, // For stockFlow
          discountPercent: l.discountPercent || 0,
          discountAmount: calc.discountAmount,
          taxable: l.taxable,
          taxRate: l.taxRate,
          taxAmount: calc.taxAmount,
          mrp: Number(l.mrp) || 0,
          lineTotal: calc.lineTotal,
          unitOfMeasure: l.unitOfMeasure || '',
          trackingType: l.trackingType || 'None',
          batchNumber: l.batchNumber || '',
          expiryDate: l.expiryDate || '',
          serialNumbers: l.serialNumbers || [],
          sourceDocId: l.sourceDocId || '',
          sourceDocNumber: l.sourceDocNumber || '',
          sourceDocDate: l.sourceDocDate || '',
          sourceLineNumber: l.sourceLineNumber || 0,
          // FLW-01: the PO line this line came from (invoice made from a PO).
          ...(Number(l.poLineNumber) > 0 ? { poLineNumber: Number(l.poLineNumber) } : {}),
          // Per-line expense account (module `purchases_line_expense_accounts`).
          // MUST be persisted here — the ledger builder reads these off the SAVED
          // line to route the line's value to the chosen expense account. Omitting
          // them (the old bug) dropped the selection on save AND made every line
          // fall back to the footer Inventory Control debit, posting no expense
          // entry. Empty for stock lines, which is correct (they go to inventory).
          expenseAccountId: l.expenseAccountId || '',
          expenseAccountCode: l.expenseAccountCode || '',
          expenseAccountName: l.expenseAccountName || ''
        };
      });

      const status = saveStatus || formData.status || 'Draft';

      const transactionData = {
        tranTypeCode: TRAN_TYPE_CODE,
        docDate: formData.docDate,
        financialYearId: activeFinancialYear.id,
        branchId: activeBranch?.id || null,
        branchName: activeBranch?.branchName || null,
        // Unified party: honor the picked party's real type (a customer selected as
        // "vendor" must post to the customer's own account for one shared ledger),
        // not a hardcoded 'vendor'.
        partyType: formData.partyType || 'vendor',
        partyId: formData.partyId,
        partyName: formData.partyName,
        vendorInvoiceNo: formData.vendorInvoiceNo,
        vendorInvoiceDate: formData.vendorInvoiceDate,
        parentId: formData.parentId || null,
        parentDocNumber: formData.parentDocNumber || '',
        parentTranType: formData.parentTranType || '',
        parentIds: formData.parentIds || [],
        poId: formData.poId,
        poNumber: formData.poNumber,
        dueDate: formData.dueDate,
        paymentTerms: formData.paymentTerms,
        currency: formData.currency,
        subtotal: totals.subtotal,
        discountAmount: totals.headerDiscount,
        taxAmount: totals.taxAmount,
        whtRate: Number(formData.whtRate) || 0,
        whtAmount: totals.whtAmount,
        transportationCharges: totals.transportationCharges,
        otherCharges: totals.otherCharges,
        totalAmount: totals.totalAmount,
        paidAmount: (Number(formData.existingPaidAmount) || 0) + (Number(formData.paymentAmount) || 0),
        // ACC-11: returns reduce the balance but are not 'paid'; never below zero.
        balanceAmount: Math.max(0, totals.totalAmount - (Number(formData.existingPaidAmount) || 0) - (Number(formData.paymentAmount) || 0) - (Number(formData.existingReturnedAmount) || 0)),
        paymentAmount: Number(formData.paymentAmount) || 0,
        paymentAccountId: formData.paymentAccountId || null,
        paymentAccountName: formData.paymentAccountName || null,
        debitAccountId: formData.debitAccountId || null,
        debitAccountCode: formData.debitAccountCode || null,
        debitAccountName: formData.debitAccountName || null,
        employeeId: formData.employeeId,
        employeeName: formData.employeeName,
        jobId: formData.jobId,
        jobName: formData.jobName,
        locationId: formData.locationId,
        locationName: formData.locationName,
        notes: formData.notes,
        status,
        lines: calculatedLines
      };

      // Add posted metadata when posting (same as SaleInvoice)
      if (status === 'Posted' && formData.status !== 'Posted') {
        transactionData.postedBy = currentUser.uid;
        transactionData.postedAt = new Date().toISOString();
      }

      // Debug: log payment data being sent
      console.log('[PI Save] Payment fields:', {
        paymentAmount: transactionData.paymentAmount,
        paymentAccountId: transactionData.paymentAccountId,
        paymentAccountName: transactionData.paymentAccountName,
        status: transactionData.status
      });

      let savedTransaction;

      if (editingInvoice) {
        // Update existing
        savedTransaction = await updateTransaction(
          activeCompany.id,
          editingInvoice.id,
          transactionData,
          currentUser.uid
        );

        // Handle status change
        if (status !== editingInvoice.status) {
          await updateTransactionStatus(activeCompany.id, editingInvoice.id, status, currentUser.uid);
        }

        savedTransaction = { ...savedTransaction, status };
      } else {
        // Create new — status is included in transactionData, so sub-transactions
        // are created with the correct status directly (no separate status update needed)
        transactionData.paidAmount = Number(formData.paymentAmount) || 0;
        transactionData.balanceAmount = totals.totalAmount - (Number(formData.paymentAmount) || 0);
        savedTransaction = await createTransaction(
          activeCompany.id,
          transactionData,
          currentUser.uid
        );
      }

      // Update each linked GRN's status to 'Invoiced' when posting.
      // For edits, first unlink GRNs that were dropped from parentIds (revert them to 'Received').
      if (formData.parentTranType === 'GRN' && status === 'Posted') {
        try {
          const newIds = formData.parentIds || [];
          const oldIds = (editingInvoice?.parentIds && editingInvoice.parentIds.length > 0)
            ? editingInvoice.parentIds
            : (editingInvoice?.parentTranType === 'GRN' && editingInvoice?.parentId ? [editingInvoice.parentId] : []);
          const removedIds = oldIds.filter(id => !newIds.includes(id));
          for (const grnId of removedIds) {
            await updateTransactionStatus(activeCompany.id, grnId, 'Received', currentUser.uid);
          }
          for (const grnId of newIds) {
            await updateTransaction(activeCompany.id, grnId, { invoicedAt: new Date().toISOString() }, currentUser.uid);
            await updateTransactionStatus(activeCompany.id, grnId, 'Invoiced', currentUser.uid);
          }
        } catch (err) { console.error('Error updating GRN status:', err); }
      }

      // Update PO line quantities and status if linked directly to PO (FLW-01).
      // Matched by the PO line each invoice line came from; an edit takes the
      // invoice's previous quantities off first. Only a posted invoice counts.
      if ((formData.parentId && formData.parentTranType === 'PO') || (editingInvoice && editingInvoice.parentTranType === 'PO')) {
        try {
          await syncInvoicePOProgress(activeCompany.id, editingInvoice,
            { parentId: formData.parentId, parentTranType: formData.parentTranType, status, lines: calculatedLines },
            currentUser.uid);
        } catch (err) { console.error('Error updating PO status:', err); }
      }

      // Optimistic UI update — show the saved record immediately so the Save
      // button releases without waiting on a full collection re-fetch.
      const savedId = editingInvoice ? editingInvoice.id : savedTransaction?.id;
      const savedRecord = editingInvoice
        ? { ...editingInvoice, ...transactionData, id: savedId, status }
        : { ...savedTransaction, status };
      applyLocal(prev => editingInvoice
        ? prev.map(r => r.id === savedId ? savedRecord : r)
        : [savedRecord, ...prev]);

      // Stock moved — drop the per-item cached quantities so the next "stock?"
      // lookup (here or on any other form this session) reads fresh numbers.
      invalidateItemStock(activeCompany.id);

      setViewMode('list');
      resetForm();

      // The GRN(s) this invoice just consumed are now 'Invoiced' — drop them from
      // the open-GRN picker so they can't be linked again this session (no re-scan
      // of the invoice history needed).
      const usedGRNIds = (savedRecord?.parentIds && savedRecord.parentIds.length > 0)
        ? savedRecord.parentIds
        : (savedRecord?.parentTranType === 'GRN' && savedRecord?.parentId ? [savedRecord.parentId] : []);
      if (savedRecord?.status === 'Posted' && usedGRNIds.length > 0) {
        const used = new Set(usedGRNIds);
        setApprovedGRNs(prev => prev.filter(g => !used.has(g.id)));
      }

      // Background refresh — reconciles with server data without blocking UI.
      loadInvoices().catch(err => console.error('Background refresh failed:', err));
    } catch (error) {
      console.error('Error saving purchase invoice:', error);
      alert('Error saving purchase invoice: ' + error.message);
    } finally {
      _screenTimer.end();
      setSaving(false);
    }
  };

  // Revert linked GRN(s) back to 'Received' when PI is deleted or cancelled.
  const reverseGRNInvoicing = async (inv) => {
    if (inv.parentTranType !== 'GRN') return;
    const ids = (inv.parentIds && inv.parentIds.length > 0)
      ? inv.parentIds
      : (inv.parentId ? [inv.parentId] : []);
    if (ids.length === 0) return;
    try {
      for (const grnId of ids) {
        await updateTransactionStatus(activeCompany.id, grnId, 'Received', currentUser.uid);
      }
    } catch (err) { console.error('Error reverting GRN status:', err); }
  };

  // Reverse PO receivedQty when PI is deleted or cancelled (only a posted
  // invoice had added to the PO; matched by the PO line each line came from).
  const reversePODelivery = async (inv) => {
    if (!inv.parentId || inv.parentTranType !== 'PO') return;
    try {
      await syncInvoicePOProgress(activeCompany.id, inv, null, currentUser.uid);
    } catch (err) { console.error('Error reversing PO delivery:', err); }
  };

  const handleDelete = async (inv) => {
    if (!canDelete) { alert('You do not have permission to delete.'); return; }
    if (inv.childIds && inv.childIds.length > 0) { alert('Cannot delete invoice with linked documents'); return; }
    if (!window.confirm(`Delete Invoice ${inv.docNumber}?`)) return;
    try {
      await deleteTransaction(activeCompany.id, inv.id);
      await reversePODelivery(inv);
      await reverseGRNInvoicing(inv);
      // D3: GRN/PO statuses changed — mark the form link-data stale so the NEXT
      // form open refetches it, instead of paying for it here on the list view.
      formDataLoadedRef.current = false;
      await loadInvoices();
    } catch (error) {
      console.error('Error deleting invoice:', error);
      alert('Error deleting invoice: ' + error.message);
    }
  };

  const handleCancel = async (inv) => {
    if (!canCancel) { alert('You do not have permission to cancel.'); return; }
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    if (inv.status === 'Paid' || inv.status === 'Cancelled') {
      alert('This invoice cannot be cancelled');
      return;
    }
    if ((inv.paidAmount || 0) > 0) {
      alert('Cannot cancel an invoice with payments. Reverse payments first.');
      return;
    }
    if (!window.confirm(`Cancel Invoice ${inv.docNumber}?`)) return;
    try {
      await updateTransactionStatus(activeCompany.id, inv.id, 'Cancelled', currentUser.uid);
      await reversePODelivery(inv);
      await reverseGRNInvoicing(inv);
      formDataLoadedRef.current = false; // D3: refresh link data on next form open
      await loadInvoices();
    } catch (error) { console.error('Error cancelling invoice:', error); }
  };

  // Print
  const handlePrint = (inv) => {
    printDocument('purchaseInvoice', inv || viewingInvoice, activeCompany, printConfig, activeBranch);
  };

  // ===== RENDER =====

  const renderListView = () => (
    <>
      <ActionBar>
        <SearchBox>
          <FiSearch />
          <input type="text" placeholder="Search by invoice#, vendor, vendor inv#..."
            value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
        </SearchBox>
        <FilterGroup>
          <StatusFilterSelect value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="All">All Status</option>
            <option value="Draft">Draft</option>
            <option value="Posted">Posted</option>
            <option value="Partial">Partial</option>
            <option value="Paid">Paid</option>
            <option value="Cancelled">Cancelled</option>
          </StatusFilterSelect>
          {canEdit && <TranAddButton onClick={handleCreate}><FiPlus /> New Invoice</TranAddButton>}
        </FilterGroup>
      </ActionBar>

      {loading ? (
        <LoadingMessage>Loading purchase invoices...</LoadingMessage>
      ) : filteredInvoices.length === 0 ? (
        <TranEmptyState>
          <FiFileText size={48} />
          <h3>No Purchase Invoices</h3>
          <p>{invoices.length === 0 ? 'Create your first purchase invoice' : 'No invoices match your filters'}</p>
          {invoices.length === 0 && canEdit && <TranAddButton onClick={handleCreate}><FiPlus /> Create Invoice</TranAddButton>}
        </TranEmptyState>
      ) : (
        <TableContainer>
          <SortContext.Provider value={sort.ctxValue}>
          <Table>
            <thead>
              <tr>
                <SortableTh field="docNumber">Invoice #</SortableTh>
                <SortableTh field="docDate">Date</SortableTh>
                {showBranchCol && <SortableTh field="branchName">Branch</SortableTh>}
                <SortableTh field="partyName">Vendor</SortableTh>
                <SortableTh field="vendorInvoiceNo">Vendor Inv#</SortableTh>
                <SortableTh field="parentDocNumber">Linked Ref</SortableTh>
                <SortableTh field="totalAmount" style={{ textAlign: 'right' }}>Total</SortableTh>
                <SortableTh field="balanceAmount" style={{ textAlign: 'right' }}>Balance</SortableTh>
                <SortableTh field="status" style={{ textAlign: 'center' }}>Status</SortableTh>
                <Th style={{ textAlign: 'center' }}>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {filteredInvoices.map(inv => (
                <Tr key={inv.id}>
                  <Td>
                    <strong>{inv.docNumber}</strong>
                    {inv.childIds?.length > 0 && (
                      <LinkedBadge>
                        <FiLink size={12} /> <LinkedDocNumbers ids={inv.childIds} showIcon={false} color="inherit" style={{ fontSize: 'inherit' }} />
                      </LinkedBadge>
                    )}
                  </Td>
                  <Td>{inv.docDate}</Td>
                  {showBranchCol && <Td>{inv.branchName || '-'}</Td>}
                  <Td>{inv.partyName}</Td>
                  <Td>{inv.vendorInvoiceNo || '-'}</Td>
                  <Td>
                    <LinkedDocNumbers {...parentLinks(inv, 'GRN')} showIcon={false} fallback={<span style={{ color: colors.text.tertiary, fontStyle: 'italic' }}>Direct</span>} />
                  </Td>
                  <Td style={{ textAlign: 'right', fontWeight: 500 }}>
                    {inv.currency} {formatAmount(inv.totalAmount)}
                  </Td>
                  <Td style={{ textAlign: 'right', fontWeight: 500, color: (inv.balanceAmount || 0) > 0 ? colors.error.main : colors.success.main }}>
                    {formatAmount(inv.balanceAmount ?? inv.totalAmount)}
                  </Td>
                  <Td style={{ textAlign: 'center' }}>
                    <StatusBadge $color={STATUS_COLORS[inv.status]}>{inv.status}</StatusBadge>
                  </Td>
                  <Td style={{ textAlign: 'center' }}>
                    <ActionGroup>
                      <IconBtn title="View" onClick={() => handleView(inv)}><FiEye /></IconBtn>
                      {canChange && inv.status !== 'Cancelled' && <IconBtn title="Edit" onClick={() => handleEdit(inv)}><FiEdit2 /></IconBtn>}
                      <IconBtn title="Print" onClick={() => handlePrint(inv)}><FiPrinter /></IconBtn>
                      {inv.status !== 'Draft' && inv.status !== 'Cancelled' && (
                        <SendWhatsAppButton templateKey="purchaseInvoice" doc={inv} party={vendors.find(v => v.id === inv.partyId)} variant="icon" />
                      )}
                      {canDelete && inv.status !== 'Cancelled' && (!inv.childIds || inv.childIds.length === 0) && (
                        <IconBtn title="Delete" $danger onClick={() => handleDelete(inv)}><FiTrash2 /></IconBtn>
                      )}
                      {canCancel && canEdit && inv.status !== 'Paid' && inv.status !== 'Cancelled' && (
                        <IconBtn title="Cancel" $danger onClick={() => handleCancel(inv)}><FiXCircle /></IconBtn>
                      )}
                    </ActionGroup>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
          </SortContext.Provider>
        </TableContainer>
      )}

      <DocListPagination {...pagination} />

      <SummaryBar>
        <span>Total: <strong>{listFiltering ? filteredInvoices.length : pagination.totalCount}</strong> invoice(s)</span>
        <span>
          Outstanding: <strong>{formatAmount(listSummary.outstanding)}</strong> |{' '}
          Draft: <strong>{listSummary.counts.Draft || 0}</strong> |{' '}
          Posted: <strong>{listSummary.counts.Posted || 0}</strong> |{' '}
          Paid: <strong>{listSummary.counts.Paid || 0}</strong>
        </span>
      </SummaryBar>
    </>
  );

  const renderViewMode = () => {
    if (!viewingInvoice) return null;
    const inv = viewingInvoice;
    return (
      <>
        <FormHeader>
          <BackButton onClick={() => { setViewMode('list'); setViewingInvoice(null); }}>
            <FiArrowLeft /> Back to List
          </BackButton>
          <FormTitle>
            {inv.docNumber}
            <StatusBadge $color={STATUS_COLORS[inv.status]} style={{ marginLeft: 12 }}>{inv.status}</StatusBadge>
          </FormTitle>
          <FormActions>
            {canChange && inv.status !== 'Cancelled' && <SaveButton onClick={() => handleEdit(inv)}><FiEdit2 /> Edit</SaveButton>}
            <SaveButton $secondary onClick={() => handlePrint(inv)}><FiPrinter /> Print</SaveButton>
          </FormActions>
        </FormHeader>

        <FormCard>
          <SectionTitle>Invoice Details</SectionTitle>
          <InfoGrid>
            <InfoItem><label>Invoice Number</label><span>{inv.docNumber}</span></InfoItem>
            <InfoItem><label>Invoice Date</label><span>{inv.docDate}</span></InfoItem>
            <InfoItem><label>Vendor</label><span>{inv.partyName}</span></InfoItem>
            <InfoItem><label>Vendor Invoice No.</label><span>{inv.vendorInvoiceNo || '-'}</span></InfoItem>
            <InfoItem><label>Vendor Invoice Date</label><span>{inv.vendorInvoiceDate || '-'}</span></InfoItem>
            <InfoItem>
              <label>{inv.parentTranType === 'PO' ? 'PO Reference' : 'GRN Reference'}</label>
              <span>
                <LinkedDocNumbers {...parentLinks(inv, 'GRN')} max={0} showIcon={false} fallback={<span style={{ color: colors.text.tertiary, fontStyle: 'italic' }}>Direct Invoice</span>} />
              </span>
            </InfoItem>
            {inv.childIds?.length > 0 && (
              <InfoItem><label>Linked Documents</label><span><LinkedDocNumbers ids={inv.childIds} max={0} showIcon={false} /></span></InfoItem>
            )}
            <InfoItem><label>PO Reference</label><span>{inv.poNumber || '-'}</span></InfoItem>
            <InfoItem><label>Due Date</label><span>{inv.dueDate || '-'}</span></InfoItem>
            <InfoItem><label>Payment Terms</label><span>{inv.paymentTerms || '-'}</span></InfoItem>
            <InfoItem><label>Currency</label><span>{inv.currency}</span></InfoItem>
            <InfoItem><label>Employee</label><span>{inv.employeeName || '-'}</span></InfoItem>
            <InfoItem><label>Job</label><span>{inv.jobName || '-'}</span></InfoItem>
            <InfoItem><label>Location</label><span>{inv.locationName || '-'}</span></InfoItem>
          </InfoGrid>
        </FormCard>

        <FormCard>
          <SectionTitle>Line Items</SectionTitle>
          <TableContainer>
            <Table>
              <thead>
                <tr>
                  <Th>#</Th><Th>Description</Th>
                  <Th style={{ textAlign: 'right' }}>Qty</Th>
                  <Th style={{ textAlign: 'right' }}>Unit Price</Th>
                  <Th style={{ textAlign: 'right' }}>Disc %</Th>
                  <Th style={{ textAlign: 'right' }}>Tax</Th>
                  <Th style={{ textAlign: 'right' }}>Total</Th>
                </tr>
              </thead>
              <tbody>
                {(inv.lines || []).map((line, i) => (
                  <Tr key={i}>
                    <Td>{i + 1}</Td><Td>{line.description}</Td>
                    <Td style={{ textAlign: 'right' }}>{line.quantity} {line.unitOfMeasure && <span style={{ fontSize: '11px', color: '#64748B' }}>{line.unitOfMeasure}</span>}<TwoUnitNote line={line} /></Td>
                    <Td style={{ textAlign: 'right' }}>{formatAmount(line.unitPrice)}</Td>
                    <Td style={{ textAlign: 'right' }}>{line.discountPercent || 0}%</Td>
                    <Td style={{ textAlign: 'right' }}>{line.taxable ? formatAmount(line.taxAmount) : '-'}</Td>
                    <Td style={{ textAlign: 'right' }}>{formatAmount(line.lineTotal)}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableContainer>
          <TotalsSection>
            <TotalsTable>
              <tbody>
                <LineDiscountRows lines={inv.lines} />
                <tr><td>Subtotal</td><td>{formatAmount(inv.subtotal)}</td></tr>
                {inv.discountAmount > 0 && <tr><td>Discount</td><td>-{formatAmount(inv.discountAmount)}</td></tr>}
                <tr><td>Tax</td><td>{formatAmount(inv.taxAmount)}</td></tr>
                {(() => {
                  const m = (inv.lines || []).reduce((s, l) => s + (Number(l.mrp) || 0), 0);
                  return m > 0 ? <tr><td>MRP</td><td>{formatAmount(m)}</td></tr> : null;
                })()}
                {inv.whtAmount > 0 && <tr><td>WHT ({inv.whtRate || 0}%)</td><td>-{formatAmount(inv.whtAmount)}</td></tr>}
                {Number(inv.transportationCharges) > 0 && <tr><td>Transportation / Carriage</td><td>{formatAmount(inv.transportationCharges)}</td></tr>}
                {Number(inv.otherCharges) > 0 && <tr><td>Other Charges</td><td>{formatAmount(inv.otherCharges)}</td></tr>}
                <TotalRow><td>Total ({inv.currency})</td><td>{formatAmount(inv.totalAmount)}</td></TotalRow>
                <tr><td>Paid</td><td style={{ color: colors.success.main }}>{formatAmount(inv.paidAmount || 0)}</td></tr>
                <BalanceRow><td>Balance Due</td><td>{formatAmount(inv.balanceAmount ?? inv.totalAmount)}</td></BalanceRow>
              </tbody>
            </TotalsTable>
          </TotalsSection>
        </FormCard>

        <AttachmentPanel
          transactionId={inv?.id}
          attachment={inv?.attachment}
          canEdit={false}
        />

        {inv.notes && <FormCard><SectionTitle>Notes</SectionTitle><NotesText>{inv.notes}</NotesText></FormCard>}
      </>
    );
  };

  const renderFormView = () => {
    // Linkable GRNs = the open ('Received') GRNs for this vendor. A GRN leaves this
    // list the moment it is invoiced (its status flips to 'Invoiced'), so no
    // cross-invoice scan is needed to exclude already-invoiced ones.
    // Only this branch's receipts / orders (see utils/branchLink.js).
    const vendorGRNs = approvedGRNs
      .filter(g => !formData.partyId || g.partyId === formData.partyId)
      .filter(g => inActiveBranch(g, activeBranch));
    const vendorPOs = approvedPOs.filter(p => !formData.partyId || p.partyId === formData.partyId)
      .filter(p => inActiveBranch(p, activeBranch, formData.parentTranType === 'PO' ? formData.parentId : null));
    const isLinked = !!formData.parentId;
    const isMultiGRN = formData.parentTranType === 'GRN' && (formData.parentIds?.length || 0) > 0;
    const linkedGRNObjects = (formData.parentIds || []).map(id => approvedGRNs.find(g => g.id === id)).filter(Boolean);
    const availableGRNs = vendorGRNs.filter(g => !formData.parentIds?.includes(g.id));
    const hasSourcedLines = lineItems.some(l => !!l.sourceDocId);

    return (
      <>
        <FormHeader>
          <BackButton onClick={() => { setViewMode('list'); resetForm(); }}>
            <FiArrowLeft /> Back to List
          </BackButton>
          <FormTitle>{editingInvoice ? `Edit: ${editingInvoice.docNumber}` : 'New Purchase Invoice'}</FormTitle>
          <FormActions>
            <SaveButton $secondary onClick={() => { setViewMode('list'); resetForm(); }}>
              <FiX /> Cancel
            </SaveButton>
            {canEdit && !(editingInvoice && editingInvoice.status && editingInvoice.status !== 'Draft' && editingInvoice.status !== 'Cancelled') && (
              <SaveButton onClick={() => handleSave('Draft')} disabled={saving}>
                <FiSave /> {saving ? 'Saving...' : 'Save Draft'}
              </SaveButton>
            )}
            {canEdit && (formData.status === 'Draft' || !editingInvoice) && (
              <SaveButton $approve onClick={() => handleSave('Posted')} disabled={saving}>
                <FiCheck /> Save & Post
              </SaveButton>
            )}
            {/* ACC-05: editing a posted document keeps it posted (no silent drop to Draft) */}
            {canEdit && (editingInvoice && editingInvoice.status && editingInvoice.status !== 'Draft' && editingInvoice.status !== 'Cancelled') && (
              <SaveButton $approve onClick={() => handleSave('Posted')} disabled={saving}><FiCheck /> {saving ? 'Saving...' : 'Save Changes'}</SaveButton>
            )}
          </FormActions>
        </FormHeader>

        <FormCard>
          <SectionTitle>Invoice Details</SectionTitle>
          <FormGrid>
            <FormGroup>
              <Label>Invoice Number</Label>
              <Input value={editingInvoice?.docNumber || 'Auto-generated'} disabled />
            </FormGroup>
            <FormGroup>
              <Label>Invoice Date *</Label>
              <Input type="date" value={formData.docDate} onChange={e => handleHeaderChange('docDate', e.target.value)} />
            </FormGroup>
            <FormGroup>
              <Label>Status</Label>
              <Input value={formData.status} disabled />
            </FormGroup>
            <FormGroup $span2>
              <Label>Link to GRN(s) {isMultiGRN ? `(${linkedGRNObjects.length})` : ''}</Label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                {linkedGRNObjects.map(g => (
                  <span key={g.id} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    background: colors.primary.main + '15', color: colors.primary.main,
                    border: `1px solid ${colors.primary.main}40`,
                    padding: '4px 8px', borderRadius: 12, fontSize: 12, fontWeight: 500
                  }}>
                    {g.docNumber}
                    <FiX
                      size={12}
                      style={{ cursor: 'pointer' }}
                      onClick={() => removeGRN(g.id)}
                      title="Remove this GRN"
                    />
                  </span>
                ))}
                <Select
                  value=""
                  onChange={e => { addGRN(e.target.value); e.target.value = ''; }}
                  disabled={formData.parentTranType === 'PO'}
                  style={{ minWidth: 160, flex: linkedGRNObjects.length === 0 ? 1 : '0 0 auto' }}
                >
                  <option value="">{linkedGRNObjects.length === 0 ? '-- Select GRN to link --' : '+ Add another GRN'}</option>
                  {availableGRNs.map(g => (
                    <option key={g.id} value={g.id}>{g.docNumber} - {g.partyName} ({g.docDate || ''})</option>
                  ))}
                </Select>
              </div>
            </FormGroup>
            {!isModuleEnabled('purchases_enforce_grn_before_pi') && (
              <FormGroup>
                <Label>Link to PO</Label>
                <Select value={formData.parentTranType === 'PO' ? formData.parentId : ''} onChange={e => handlePOChange(e.target.value)} disabled={isMultiGRN}>
                  <option value="">-- No PO --</option>
                  {vendorPOs.map(p => (
                    <option key={p.id} value={p.id}>{p.docNumber} - {p.partyName}</option>
                  ))}
                </Select>
              </FormGroup>
            )}
            <FormGroup $span2>
              <Label>Vendor *</Label>
              <VendorSelect vendors={vendors} selectedVendorId={formData.partyId} onChange={handleVendorChange} disabled={isLinked || isMultiGRN} balanceOnDemand />
            </FormGroup>
            <FormGroup>
              <Label>Vendor Invoice No. *</Label>
              <Input value={formData.vendorInvoiceNo} onChange={e => handleHeaderChange('vendorInvoiceNo', e.target.value)} placeholder="Vendor's invoice #" />
            </FormGroup>
            <FormGroup>
              <Label>Vendor Invoice Date</Label>
              <Input type="date" value={formData.vendorInvoiceDate} onChange={e => handleHeaderChange('vendorInvoiceDate', e.target.value)} />
            </FormGroup>
            <FormGroup>
              <Label>Due Date</Label>
              <Input type="date" value={formData.dueDate} onChange={e => handleHeaderChange('dueDate', e.target.value)} />
            </FormGroup>
            <FormGroup>
              <Label>Payment Terms</Label>
              <Input value={formData.paymentTerms} onChange={e => handleHeaderChange('paymentTerms', e.target.value)} placeholder="e.g., 30 days" />
            </FormGroup>
            <FormGroup>
              <Label>PO Reference</Label>
              <Input value={formData.poNumber} disabled placeholder="From linked GRN/PO" />
            </FormGroup>
            <FormGroup>
              <Label>Currency</Label>
              <Input value={formData.currency} disabled />
            </FormGroup>
            <FormGroup>
              <Label>Employee</Label>
              <Select value={formData.employeeId} onChange={e => {
                const emp = employees.find(em => em.id === e.target.value);
                handleHeaderChange('employeeId', e.target.value);
                handleHeaderChange('employeeName', emp ? `${emp.firstName || ''} ${emp.lastName || ''}`.trim() : '');
              }}>
                <option value="">-- Select Employee --</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>{emp.employeeCode ? emp.employeeCode + ' - ' : ''}{emp.firstName} {emp.lastName}</option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup>
              <Label>Job</Label>
              <Select value={formData.jobId} onChange={e => {
                const job = jobs.find(j => j.id === e.target.value);
                handleHeaderChange('jobId', e.target.value);
                handleHeaderChange('jobName', job?.jobName || '');
              }}>
                <option value="">-- Select Job --</option>
                {jobs.map(job => (
                  <option key={job.id} value={job.id}>{job.jobCode ? job.jobCode + ' - ' : ''}{job.jobName}</option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup>
              <Label>{reqLabel(activeCompany, 'Location')}</Label>
              <Select value={formData.locationId} onChange={e => {
                const loc = locations.find(l => l.id === e.target.value);
                handleHeaderChange('locationId', e.target.value);
                handleHeaderChange('locationName', loc?.locationName || '');
              }} disabled={!!formData.parentId && !(locationCompulsory(activeCompany) && (formData.parentTranType === 'PO' || !formData.locationId))}>
                <option value="">-- Select Location --</option>
                {locations.map(loc => (
                  <option key={loc.id} value={loc.id}>{loc.locationName}</option>
                ))}
              </Select>
            </FormGroup>
          </FormGrid>
        </FormCard>

        <FormCard>
          <LineItemsHeader>
            <SectionTitle style={{ margin: 0 }}>Line Items</SectionTitle>
            {!formData.parentId && !isMultiGRN && <AddLineButton onClick={addLine}><FiPlus /> Add Row</AddLineButton>}
          </LineItemsHeader>

          <LineTableContainer>
            <LineTable>
              <thead>
                <tr>
                  <LineTh style={{ width: 50, minWidth: 50, maxWidth: 50 }}>#</LineTh>
                  {hasSourcedLines && <LineTh style={{ width: 130, minWidth: 130, maxWidth: 130 }}>GRN #</LineTh>}
                  {hasSourcedLines && <LineTh style={{ width: 110, minWidth: 110, maxWidth: 110 }}>GRN Date</LineTh>}
                  <LineTh style={{ width: 220, minWidth: 220, maxWidth: 220 }}>Item</LineTh>
                  {lineExpenseAccEnabled && <LineTh style={{ width: 200, minWidth: 200, maxWidth: 200 }}>Expense Account</LineTh>}
                  <LineTh style={{ width: 260, minWidth: 260, maxWidth: 260 }}>Description</LineTh>
                  <LineTh style={{ width: 120, minWidth: 120, maxWidth: 120 }}>Qty</LineTh>
                  <LineTh style={{ width: 140, minWidth: 140, maxWidth: 140 }}>Unit Price</LineTh>
                  <LineTh style={{ width: 100, minWidth: 100, maxWidth: 100 }}>Disc % / Amt</LineTh>
                  <LineTh style={{ width: 80, minWidth: 80, maxWidth: 80 }}>Tax?</LineTh>
                  <LineTh style={{ width: 100, minWidth: 100, maxWidth: 100 }}>Tax %</LineTh>
                  {mrpEnabled && <LineTh style={{ width: 120, minWidth: 120, maxWidth: 120 }}>MRP</LineTh>}
                  <LineTh style={{ width: 140, minWidth: 140, maxWidth: 140 }}>Total</LineTh>
                  {lineItems.some(l => l.trackingType === 'Batch') && <LineTh style={{ width: 140, minWidth: 140, maxWidth: 140 }}>Batch #</LineTh>}
                  {lineItems.some(l => l.trackingType === 'Batch') && <LineTh style={{ width: 150, minWidth: 150, maxWidth: 150 }}>Expiry Date</LineTh>}
                  {lineItems.some(l => l.trackingType === 'Serial') && <LineTh style={{ width: 220, minWidth: 220, maxWidth: 220 }}>Serial Numbers</LineTh>}
                  <LineTh style={{ width: 50, minWidth: 50, maxWidth: 50 }}></LineTh>
                </tr>
              </thead>
              <tbody>
                {lineItems.map((line, index) => {
                  const calc = calculateLine(line);
                  const isSourced = !!line.sourceDocId;
                  return (
                    <React.Fragment key={index}><tr>
                      <LineTd style={{ textAlign: 'center', color: colors.text.secondary }}>{index + 1}</LineTd>
                      {hasSourcedLines && <LineTd style={{ fontSize: 11, color: colors.text.secondary }}>{line.sourceDocNumber || '-'}</LineTd>}
                      {hasSourcedLines && <LineTd style={{ fontSize: 11, color: colors.text.secondary }}>{line.sourceDocDate || '-'}</LineTd>}
                      <LineTd>
                        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <ItemSelect ref={(el) => { lineItemRefs.current[index] = el; }} items={items} selectedItemId={line.itemId} onChange={(itemId) => handleLineChange(index, 'itemId', itemId)} disabled={!!formData.parentId || isSourced} stockBalances={stockBalances} stockOnDemand onRequestStock={requestItemStock} />
                          </div>
                          {!(!!formData.parentId || isSourced) && (
                            <button type="button" title="Quick add new item"
                              onClick={() => { setQuickAddIndex(index); setShowQuickAdd(true); }}
                              style={{ flexShrink: 0, width: 26, height: 26, border: '1px solid #ccc', borderRadius: 6, background: '#fff', color: '#800020', fontSize: 16, fontWeight: 700, cursor: 'pointer', lineHeight: 1 }}>+</button>
                          )}
                        </div>
                        {line.itemId && <ItemPriceHints hints={priceRefs[line.itemId]} />}
                      </LineTd>
                      {lineExpenseAccEnabled && (
                        <LineTd>
                          {isServiceLine(line) ? (
                            <Select
                              value={line.expenseAccountId || ''}
                              onChange={e => handleLineChange(index, 'expenseAccountId', e.target.value)}
                              disabled={!!formData.parentId || isSourced}
                              style={{ padding: '4px 6px', fontSize: 12, width: '100%' }}
                            >
                              <option value="">-- Default (footer) --</option>
                              {expenseAccounts.map(a => (
                                <option key={a.id} value={a.id}>{a.code} - {a.name}</option>
                              ))}
                            </Select>
                          ) : (
                            <span style={{ fontSize: 11, color: '#bbb' }}
                              title="Only service (non-stock) items can be posted to an expense account — stock items must go to inventory.">
                              —
                            </span>
                          )}
                        </LineTd>
                      )}
                      <LineTd>
                        <LineInput value={line.description} onChange={e => handleLineChange(index, 'description', e.target.value)}
                          placeholder="Description" disabled={!!formData.parentId || isSourced} />
                        
                      </LineTd>
                      <LineTd>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <LineInput type="number" min="0" value={line.quantity}
                            onChange={e => handleLineChange(index, 'quantity', Number(e.target.value))}
                            disabled={isSourced || line.hasSize}
                            title={line.hasSize ? 'Auto-computed from L × H × Pieces' : undefined}
                            style={{ textAlign: 'right', background: line.hasSize ? '#F1F5F9' : undefined }} />
                          {(() => {
                            const itm = items.find(i => i.id === line.itemId);
                            const itmUnits = itm && Array.isArray(itm.units) ? itm.units : [];
                            if (multiUnitEnabled && itmUnits.length > 1) {
                              return (
                                <Select
                                  value={line.unitCode || line.unitOfMeasure || ''}
                                  onChange={e => handleLineChange(index, 'unitCode', e.target.value)}
                                  disabled={!!formData.parentId}
                                  style={{ padding: '2px 4px', fontSize: '11px', minWidth: 56 }}
                                >
                                  {itmUnits.map(u => (
                                    <option key={u.unitCode} value={u.unitCode}>{u.unitCode}</option>
                                  ))}
                                </Select>
                              );
                            }
                            return line.unitOfMeasure ? (
                              <span style={{ fontSize: '11px', color: '#64748B', whiteSpace: 'nowrap' }}>{line.unitOfMeasure}</span>
                            ) : null;
                          })()}
                        </div>
                        <TwoUnitQtyBox item={items.find(i => i.id === line.itemId)} unitCode={line.unitCode || line.unitOfMeasure}
                          quantity={line.quantity} hasSize={line.hasSize} disabled={isSourced}
                          onChange={(q) => handleLineChange(index, 'quantity', q)} />
                      </LineTd>
                      <LineTd>
                        <LineInput type="number" min="0" step="0.01" value={line.unitPrice}
                          onChange={e => handleLineChange(index, 'unitPrice', Number(e.target.value))} style={{ textAlign: 'right' }} />
                      </LineTd>
                      <LineTd>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          <LineInput type="number" min="0" max="100" step="0.01" value={line.discountPercent}
                            onChange={e => handleLineChange(index, 'discountPercent', Number(e.target.value))} style={{ textAlign: 'right' }} title="Discount %" placeholder="%" />
                          <LineInput type="number" min="0" step="0.01" value={calc.discountAmount || 0}
                            onChange={e => handleLineChange(index, 'discountAmountInput', Number(e.target.value))} style={{ textAlign: 'right' }} title="Discount amount" placeholder="Amt" />
                        </div>
                      </LineTd>
                      <LineTd style={{ textAlign: 'center' }}>
                        <input type="checkbox" checked={line.taxable} onChange={e => handleLineChange(index, 'taxable', e.target.checked)}
                          onKeyDown={(e) => { if (e.key === 'Enter' && !line.taxable) handleLastFieldEnter(e, index); }} />
                      </LineTd>
                      <LineTd>
                        <LineInput type="number" min="0" max="100" value={line.taxRate}
                          onChange={e => handleLineChange(index, 'taxRate', Number(e.target.value))}
                          onKeyDown={(e) => handleLastFieldEnter(e, index)}
                          disabled={!line.taxable} style={{ textAlign: 'right', opacity: line.taxable ? 1 : 0.4 }} />
                      </LineTd>
                      {mrpEnabled && (
                        <LineTd>
                          <LineInput type="number" min="0" step="0.01" value={line.mrp || 0}
                            onChange={e => handleLineChange(index, 'mrp', Number(e.target.value))}
                            style={{ textAlign: 'right' }} />
                        </LineTd>
                      )}
                      <LineTd style={{ textAlign: 'right', fontWeight: 600 }}>{formatAmount(calc.lineTotal)}</LineTd>
                      {lineItems.some(l => l.trackingType === 'Batch') && (
                        <LineTd>{line.trackingType === 'Batch' ? <LineInput value={line.batchNumber} onChange={e => handleLineChange(index, 'batchNumber', e.target.value)} placeholder="Batch #" /> : <span style={{ color: colors.text.secondary }}>-</span>}</LineTd>
                      )}
                      {lineItems.some(l => l.trackingType === 'Batch') && (
                        <LineTd>{line.trackingType === 'Batch' ? <LineInput type="date" value={line.expiryDate || ''} onChange={e => handleLineChange(index, 'expiryDate', e.target.value)} /> : <span style={{ color: colors.text.secondary }}>-</span>}</LineTd>
                      )}
                      {lineItems.some(l => l.trackingType === 'Serial') && (
                        <LineTd>{line.trackingType === 'Serial' ? <LineInput value={line.serialText !== undefined ? line.serialText : (line.serialNumbers || []).join(', ')} onChange={e => handleLineChange(index, 'serialText', e.target.value)} onBlur={e => { const parsed = e.target.value.split(',').map(s => s.trim()).filter(Boolean); handleLineChange(index, 'serialNumbers', parsed); handleLineChange(index, 'serialText', undefined); }} placeholder={`${line.quantity || 0} serial(s), comma-separated`} /> : <span style={{ color: colors.text.secondary }}>-</span>}</LineTd>
                      )}
                      <LineTd style={{ textAlign: 'center' }}>
                        {!formData.parentId && lineItems.length > 1 && <RemoveLineBtn onClick={() => removeLine(index)} title="Remove"><FiX /></RemoveLineBtn>}
                      </LineTd>
                    </tr>
                    {line.hasSize && (<SizeLineRow line={line} index={index} onChange={handleLineChange} disabled={!!formData.parentId || isSourced} leftColSpan={2 + (hasSourcedLines ? 2 : 0) + (lineExpenseAccEnabled ? 1 : 0)} />)}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </LineTable>
          </LineTableContainer>

          <TotalsSection>
            <TotalsTable>
              <tbody>
                <LineDiscountRows lines={lineItems.map(calculateLine)} />
                <tr><td>Subtotal</td><td>{formatAmount(totals.subtotal)}</td></tr>
                <tr>
                  <td>Discount</td>
                  <td><DiscountInput type="number" min="0" step="0.01" value={formData.discountAmount} onChange={e => handleHeaderChange('discountAmount', Number(e.target.value))} /></td>
                </tr>
                <tr><td>Tax</td><td>{formatAmount(totals.taxAmount)}</td></tr>
                {mrpEnabled && totals.mrpTotal > 0 && (
                  <tr><td>MRP</td><td>{formatAmount(totals.mrpTotal)}</td></tr>
                )}
                <tr>
                  <td>WHT %</td>
                  <td><DiscountInput type="number" min="0" max="100" step="0.5" value={formData.whtRate} onChange={e => handleHeaderChange('whtRate', Number(e.target.value))} /></td>
                </tr>
                {totals.whtAmount > 0 && <tr><td>WHT Amount</td><td>-{formatAmount(totals.whtAmount)}</td></tr>}
                <tr>
                  <td>Transportation / Carriage</td>
                  <td><DiscountInput type="number" min="0" step="0.01" value={formData.transportationCharges} onChange={e => handleHeaderChange('transportationCharges', Number(e.target.value))} /></td>
                </tr>
                <tr>
                  <td>Other Charges</td>
                  <td><DiscountInput type="number" min="0" step="0.01" value={formData.otherCharges} onChange={e => handleHeaderChange('otherCharges', Number(e.target.value))} /></td>
                </tr>
                <TotalRow><td>Total ({formData.currency})</td><td>{formatAmount(totals.totalAmount)}</td></TotalRow>
              </tbody>
            </TotalsTable>
          </TotalsSection>

          <PaymentSection>
            <SectionTitle>Payment</SectionTitle>
            <FormGrid>
              {formData.existingPaidAmount > 0 && (
                <FormGroup>
                  <Label>Paid via Vouchers</Label>
                  <Input value={formatAmount(formData.existingPaidAmount)} disabled
                    style={{ fontWeight: 600, color: colors.success.main }} />
                </FormGroup>
              )}
              <FormGroup>
                <Label>Amount Paid</Label>
                <Input type="number" min="0" step="0.01" value={formData.paymentAmount}
                  onChange={e => handleHeaderChange('paymentAmount', Math.min(Number(e.target.value), totals.totalAmount - formData.existingPaidAmount))}
                  placeholder="0.00" />
              </FormGroup>
              <FormGroup>
                <Label>Payment Account</Label>
                <Select value={formData.paymentAccountId} onChange={e => {
                  const acc = paymentAccounts.find(a => a.id === e.target.value);
                  handleHeaderChange('paymentAccountId', e.target.value);
                  handleHeaderChange('paymentAccountName', acc?.name || '');
                }}>
                  <option value="">-- Select Account --</option>
                  <optgroup label="Cash Accounts">
                    {paymentAccounts.filter(a => a.code?.startsWith('11101')).map(a => (
                      <option key={a.id} value={a.id}>{a.code} - {a.name}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Bank Accounts">
                    {paymentAccounts.filter(a => a.code?.startsWith('11102')).map(a => (
                      <option key={a.id} value={a.id}>{a.code} - {a.name}</option>
                    ))}
                  </optgroup>
                </Select>
              </FormGroup>
              <FormGroup>
                <Label>Debit Account (override)</Label>
                <Select value={formData.debitAccountId} onChange={e => {
                  const acc = allAccounts.find(a => a.id === e.target.value);
                  handleHeaderChange('debitAccountId', e.target.value);
                  handleHeaderChange('debitAccountCode', acc?.code || '');
                  handleHeaderChange('debitAccountName', acc?.name || '');
                }}>
                  <option value="">-- Default: Inventory Control --</option>
                  {allAccounts.map(a => (
                    <option key={a.id} value={a.id}>{a.code} - {a.name}</option>
                  ))}
                </Select>
              </FormGroup>
              {(formData.existingPaidAmount > 0 || Number(formData.paymentAmount) > 0) && (
                <FormGroup>
                  <Label>Balance After Payment</Label>
                  <Input value={formatAmount(totals.totalAmount - formData.existingPaidAmount - (Number(formData.paymentAmount) || 0))} disabled
                    style={{ fontWeight: 600, color: colors.error.main }} />
                </FormGroup>
              )}
            </FormGrid>
          </PaymentSection>
        </FormCard>

        <FormCard>
          <SectionTitle>Additional Information</SectionTitle>
          <FormGrid>
            <FormGroup $span2>
              <Label>Notes</Label>
              <TextArea value={formData.notes} onChange={e => handleHeaderChange('notes', e.target.value)} placeholder="Internal notes..." rows={3} />
            </FormGroup>
          </FormGrid>
        </FormCard>

        <AttachmentPanel
          transactionId={editingInvoice?.id}
          attachment={editingInvoice?.attachment}
          canEdit={canEdit}
          onChange={({ attachment }) => setEditingInvoice(prev => prev ? { ...prev, attachment } : prev)}
        />
      </>
    );
  };

  useDocumentShortcuts({
    onNew: canEdit ? handleCreate : undefined,
    onSave: canEdit ? () => handleSave('Posted') : undefined,
    viewMode,
    saving
  });

  return (
    <PageLayout pageTitle="Purchase Invoice" breadcrumb="Purchases / Purchase Invoice">
      {viewMode === 'list' && renderListView()}
      {viewMode === 'form' && renderFormView()}
      {viewMode === 'view' && renderViewMode()}
      {showQuickAdd && (
        <QuickAddItemModal
          companyId={activeCompany?.id}
          existingItems={items}
          onCreated={handleQuickItemCreated}
          onClose={() => { setShowQuickAdd(false); setQuickAddIndex(null); }}
        />
      )}
    </PageLayout>
  );
};

// ============ LOCAL STYLED COMPONENTS ============
const LinkedBadge = styled.span`display:inline-flex;align-items:center;gap:2px;margin-left:8px;padding:2px 6px;background:${colors.info.main}15;color:${colors.info.main};border-radius:4px;font-size:10px;font-weight:600;`;

export default PurchaseInvoice;
